<script lang="ts">
  import { getCommonTranslations } from '@athena-lib/commonTranslationsContext';
  import { Dropdown } from '@athena-lib/Fields';
  import { Modal } from '@athena-lib/Modal';
  import { Button } from '@athena-lib/Button';
  import { Paragraph } from '@athena-lib/Text';
  import LocationPin from '@athena-lib/Icons/LocationPin.svelte';
  import type { CheckoutSdk } from '@athena-lib/Checkout/utils';
  import clsx from 'clsx';
  import { onMount } from 'svelte';
  import KeyboardArrowRight from '@athena-lib/Icons/KeyboardArrowRight.svelte';
  import type { ShippingCountry } from '@stickerapp-org/checkout-sdk';
  import { Text } from '@athena-lib/Text';
  import type { TextSize } from '@athena-lib/Text/Text/types';

  let {
    type,
    checkoutSdk,
    onUpdateCountry,
    currentCountry,
    size = 'medium',
    boldPostalCode = false,
    onOpenCountryPicker,
  }: {
    type: 'checkout' | 'menu' | 'shippingBanner' | 'estimatedDelivery';
    checkoutSdk: CheckoutSdk | null;
    onUpdateCountry: (code: string, currency: string) => void;
    currentCountry?: string;
    size?: TextSize;
    boldPostalCode?: boolean;
    onOpenCountryPicker?: () => void;
  } = $props();

  let countries = $state<ShippingCountry[] | undefined>(undefined);

  let country = $derived.by(() => {
    return countries && currentCountry
      ? countries.find((c) => c.code.toLowerCase() === currentCountry.toLowerCase())?.name || ''
      : '';
  });

  onMount(async () => {
    if (checkoutSdk) {
      countries = (await checkoutSdk.countries()).shippingCountries.map((c) => ({
        name: c.name,
        code: c.code,
        active: c.active,
        currency: c.currency,
      }));
    }
  });

  const translations = getCommonTranslations();
  let shipToAnotherModal = $state<{ type: 'open'; value: string } | { type: 'closed' }>({
    type: 'closed',
  });
</script>

{#if type === 'menu'}
  <button
    type="button"
    onclick={() => {
      shipToAnotherModal = {
        type: 'open',
        value: currentCountry ? currentCountry.toLowerCase() : '',
      };
    }}
    class="flex items-center gap-2 text-st-gray-300"
  >
    <LocationPin sizeClass="size-4" />

    <span class="line-clamp-1">{country || translations.shipTo}</span>
  </button>
{:else if type === 'checkout'}
  <div
    class={clsx(
      'relative',
      'h-12',
      'rounded',
      'bg-white',
      'border',
      'has-[:focus]:border-st-blue-200 has-[:focus]:ring has-[:focus]:ring-st-blue-100',
      'inline-block border-st-gray-500 md:basis-4/12',
    )}
  >
    <button
      id="ship-to-button"
      type="button"
      class="flex h-full w-full items-center pl-3"
      onclick={() => {
        shipToAnotherModal = {
          type: 'open',
          value: currentCountry ? currentCountry.toLowerCase() : '',
        };
      }}
    >
      <img
        src={'/api/asset-flags/' + (currentCountry?.toLowerCase() || 'us')}
        alt={`Flag of ${currentCountry?.toLowerCase()}`}
        class="h-6 w-8 rounded"
        loading="lazy"
      />
      <span class="line-clamp-1 pl-2 text-left">{country || translations.shipTo}</span>
      <span class="absolute right-2.5 top-2.5">
        <KeyboardArrowRight sizeClass="h-6 w-6" />
      </span>
    </button>
  </div>
{:else if type === 'shippingBanner'}
  <button
    type="button"
    class="flex items-center gap-2 text-st-gray-300"
    onclick={() => {
      shipToAnotherModal = {
        type: 'open',
        value: currentCountry ? currentCountry.toLowerCase() : '',
      };
    }}
  >
    <LocationPin sizeClass="size-4" />
    <Text element="span" size="xsmall" color="light">{country}</Text>
  </button>
{:else if type === 'estimatedDelivery'}
  <button
    type="button"
    onclick={() => {
      if (onOpenCountryPicker) {
        return onOpenCountryPicker();
      } else {
        shipToAnotherModal = {
          type: 'open',
          value: currentCountry ? currentCountry.toLowerCase() : '',
        };
      }
    }}
  >
    <Text element="span" {size} class="underline" bold={boldPostalCode}
      >{country || translations.shipTo}</Text
    >
  </button>
{/if}
{#if countries}
  <Modal
    showModal={shipToAnotherModal.type === 'open'}
    onClose={() => {
      shipToAnotherModal = { type: 'closed' };
    }}
    size="sm"
    closable={true}
    class="!z-40"
  >
    {#if shipToAnotherModal.type === 'open'}
      <div class="flex flex-col gap-4 p-4">
        <Paragraph size="large" class="text-center" bold
          >{translations.shipToAnotherCountryTitle}</Paragraph
        >
        <Paragraph class="pb-6 text-center">{translations.shipToAnotherCountryDisclaimer}</Paragraph
        >
        <Dropdown
          name="country"
          class="text-st-black"
          bind:value={shipToAnotherModal.value}
          options={countries?.map((x) => ({
            label: x.name,
            value: x.code.toLowerCase(),
            disabled: !x.active,
          })) ?? []}
          country={shipToAnotherModal.value?.toLowerCase()}
        />
        <Button
          type="button"
          onClick={() => {
            const needle =
              shipToAnotherModal.type === 'open' ? shipToAnotherModal.value : undefined;
            const foundCountry = countries?.find((x) => x.code.toLowerCase() === needle);
            if (foundCountry) {
              onUpdateCountry(foundCountry.code, foundCountry.currency);
              shipToAnotherModal = { type: 'closed' };
            }
          }}>{translations.continue}</Button
        >
      </div>
    {/if}
  </Modal>
{/if}
